import { useEffect, useMemo, useRef, useState } from "react";
import { shallow } from "zustand/shallow";

import { cn } from "@/lib/chillnn/cn";

import { BodyWrapper } from "@/components/ui/body/body-wrapper";

import { EditGuideTooltip } from "@/components/features-new/custom-mail-setting/default/components/tooltip/edit-guide-tooltip";

import {
	FeatureIslandCategoryContext,
	useFeatureIslandCategoryContext,
	useFeatureIslandCategoryManagerContext,
} from "./context";

import type { FeatureIslandCategoryStoreType } from "./store";
import type { StoreApi } from "zustand";

type Props = {
	children: React.ReactNode;
	categoryStore: StoreApi<FeatureIslandCategoryStoreType>;
	islandTitle?: string;
	tooltipTitle?: string;
};

/**
 * アイテムの編集画面でカテゴリーごとにラップするコンポーネント
 * １つのカテゴリーでも複数の `<EditPageCategoryWrapper />` が存在しうる
 *
 * `<Question />` コンポーネントで ` useEditPageCategoryContext() `を呼び出すために `categoryStore` を Provide している
 *
 * @param categoryStore カテゴリー単体のStore を渡すことで、ラップされた `<Question~ />` 内部でアイテムの状態を取得・更新できる。主に編集ステータス情報を更新するために使用している。
 * @param islandTitle カテゴリー内の島ごとのタイトル（必ずしもカテゴリーのタイトルと一致はしない）。デフォルトではカテゴリーのタイトルが入る
 */
export const FeatureIslandMainWithCategoryWrapper = ({ children, categoryStore, islandTitle, tooltipTitle }: Props) => {
	const store = useRef(categoryStore).current;

	return (
		<FeatureIslandCategoryContext.Provider value={store}>
			<FeatureIslandMainWithCategoryWrapperInner islandTitle={islandTitle} tooltipTitle={tooltipTitle}>
				{children}
			</FeatureIslandMainWithCategoryWrapperInner>
		</FeatureIslandCategoryContext.Provider>
	);
};

const FeatureIslandMainWithCategoryWrapperInner = ({
	children,
	islandTitle,
	tooltipTitle,
}: Omit<Props, "categoryStore">) => {
	const categoryID = useFeatureIslandCategoryContext((state) => state.categoryID, shallow);
	const title = useFeatureIslandCategoryContext((state) => state.title, shallow);

	const activeCategoryID = useFeatureIslandCategoryManagerContext((state) => state.activeCategoryID, shallow);

	const [rendered, setRendered] = useState(false);
	useEffect(() => {
		setRendered(true);
	}, []);

	const canShow = useMemo(() => {
		// 初回レンダリング時は全てのカテゴリーを表示し、編集・エラーステータスを反映させる
		if (!rendered) {
			return true;
		}

		return activeCategoryID === categoryID;
	}, [activeCategoryID, categoryID, rendered]);

	return (
		canShow && (
			<div className={cn("flex w-full flex-col gap-y-3")}>
				<div className="flex items-center gap-x-2">
					<h4 className="text-lg font-semibold">{islandTitle || title}</h4>
					{tooltipTitle && <EditGuideTooltip guide={tooltipTitle} />}
				</div>

				<BodyWrapper>
					<section className="flex w-full flex-col gap-y-12 divide-y divide-zinc-100 [&>*:not(:first-child)]:pt-6">
						{children}
					</section>
				</BodyWrapper>
			</div>
		)
	);
};
